import { memo, useCallback } from "react";
import { MARKIT_DOCS } from "@markit/common.utils";
import {
  FlowCodeCard,
  FlowFoldIntro,
  FlowResultRow,
  FlowSourceRow,
  stageLabel,
  wireConnector,
} from "./SubComponents/IntegrationFlowParts";
import { Colors } from "../../utils/colors";

type ApiIntegrationsFoldProps = {
  isDesktopSize: boolean;
};

const codeLine = (
  keyName: string,
  value: string,
  valueColor: string,
  trailingComma: boolean
) => (
  <div>
    <span style={{ color: "#6badff" }}>{`"${keyName}"`}</span>
    <span style={{ color: "#858a99" }}>{": "}</span>
    <span style={{ color: valueColor }}>{`"${value}"`}</span>
    {trailingComma ? <span style={{ color: "#858a99" }}>,</span> : null}
  </div>
);

const ApiIntegrationsFold = (props: ApiIntegrationsFoldProps) => {
  const { isDesktopSize } = props;

  const readDocsOnPress = useCallback(() => {
    window.open(MARKIT_DOCS + "/webhooksapi", "_blank");
  }, []);

  return (
    <div
      style={{
        borderRadius: isDesktopSize ? 24 : 16,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <FlowFoldIntro
        isDesktopSize={isDesktopSize}
        badgeIcon={
          <span style={{ color: Colors.BLUE5, fontSize: 13, fontWeight: 600 }}>
            {"</>"}
          </span>
        }
        badgeLabel="Integrate with Everything"
        title="Plug Markit into your stack"
        body="Every form submission and incoming text can fire a webhook the moment it happens. Sync contacts and texts with the tools you already use. No exports, no copy-paste."
        ctaLabel="Read the Docs"
        ctaOnPress={readDocsOnPress}
        chipPrefix="$"
        chipText="curl https://api.markitai.com/v1"
      />
      <div
        className={
          isDesktopSize ? "AlignedRow slide-up" : "ColumnNormal slide-up"
        }
        style={{
          marginTop: isDesktopSize ? 64 : 48,
          gap: isDesktopSize ? 4 : 10,
          alignItems: isDesktopSize ? "flex-start" : "stretch",
        }}
      >
        <div
          className="ColumnNormal"
          style={{ gap: 12, flex: 1, width: "100%" }}
        >
          {stageLabel("1 — IT STARTS ON MARKIT", isDesktopSize)}
          <div
            className="ColumnNormal"
            style={{ gap: 12, width: "100%", marginTop: 12 }}
          >
            <FlowSourceRow
              icon={<span style={{ fontSize: 17 }}>🎟️</span>}
              title="New RSVP"
              subtitle="event.rsvp"
              time="now"
              highlighted
            />
            <FlowSourceRow
              icon={<span style={{ fontSize: 17 }}>📋</span>}
              title="Form submitted"
              subtitle="form.submission"
              time="2m"
            />
            <FlowSourceRow
              icon={<span style={{ fontSize: 17 }}>💬</span>}
              title="Text received"
              subtitle="conversations.inbound"
              time="5m"
            />
          </div>
        </div>
        {wireConnector(isDesktopSize)}
        <div
          className="ColumnNormal"
          style={{ gap: 12, flex: 1, width: "100%" }}
        >
          {stageLabel("2 — A WEBHOOK FIRES INSTANTLY", isDesktopSize)}
          <div style={{ marginTop: 12, width: "100%" }}>
            <FlowCodeCard
              label="POST"
              url="your-server.com/hooks"
              footerText="Signed with your webhook secret"
            >
              <div>{"{"}</div>
              {codeLine("type", "event.rsvp", "#4d9eff", true)}
              {codeLine("event", "Boston Run Club 5K", "#9ee0a6", true)}
              {codeLine("attendee", "Sarah Chen", "#9ee0a6", true)}
              {codeLine("phone", "+1 (617) 555-0134", "#9ee0a6", true)}
              {codeLine("ticket", "VIP — $25.00", "#9ee0a6", false)}
              <div>{"}"}</div>
            </FlowCodeCard>
          </div>
        </div>
        {wireConnector(isDesktopSize)}
        <div
          className="ColumnNormal"
          style={{ gap: 12, flex: 1, width: "100%" }}
        >
          {stageLabel("3 — LANDS IN YOUR STACK", isDesktopSize)}
          <div
            className="ColumnNormal"
            style={{ gap: 12, width: "100%", marginTop: 12 }}
          >
            <FlowResultRow
              logoBg="#3b82f5"
              logoText="C"
              title="Your CRM"
              status="Contact synced"
            />
            <FlowResultRow
              logoBg="#ff4f00"
              logoText="Z"
              title="Zapier"
              status="Zap triggered"
            />
            <FlowResultRow
              logoBg="#33a854"
              logoText="G"
              title="Google Sheets"
              status="Row added"
            />
            <FlowResultRow
              logoBg="#4a144a"
              logoText="#"
              title="Slack"
              status="Alert posted"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(ApiIntegrationsFold);
